import type { IAddCancellationCmd, IAddCancellationPositionData } from "@/data/commands/order/AddCancellationCmd";
import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import type { ICancellationOrderItemDraft, ICancellationOrderItemPositionDraft, ICancellationPositionPayment } from "@/data/model/Cancellation";
import { CancellationMode, CancellationPositionType, RefundMode, RefundabilityType } from "@/data/model/Enums";
import type { IFullOrder } from "@/data/model/Order";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";



import { BaseModal } from "@/shared/elements/BaseModal";
import { translate } from "@/shared/localization/language";
import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";
import { PaymentMethodUtil } from "@/shared/utils/PaymentMethodUtil";



import { NumberInput } from "@/portal/formControls/NumberInput";
import { Switch } from "@/portal/formControls/Switch";
import { showSelectCancellationReasonModal } from "@/portal/modal/CancellationWindowUtils";
import { UiNotification } from "@/portal/ui/Notification";


export class CancellationPositionsWindow extends BaseModal {
    private readonly $form: JQuery;
    private $submitBtn: JQuery;

    private amountInput?: NumberInput;
    
    private $refundContainer: JQuery;
    private refundToggle: Switch;
    private $refundToggleContainer: JQuery;
    
    private refundModeToggle: Switch;
    private $refundModeToggleContainer: JQuery;
    
    private $positions?: JQuery;
    private orderId: string;
    private isAdmin: boolean;
    private doNotShowRefundToggles: boolean;

    private cancellationPositions: Array<ICancellationOrderItemDraft> = [];
    private order?: IFullOrder;
    private cancellableAmount?: number; //get updated on button validation

    private dao: OrderDao;

    private onOrderCancellationSucceeded: () => void;
    private onOrderCancellationFailed: (errorCode: string) => void;

    constructor(
        orderId: string,
        isAdmin: boolean,
        doNotShowRefundToggles: boolean,
        onOrderCancellationSucceeded: () => void,
        onOrderCancellationFailed: (errorCode: string) => void
    ) {
        super(
            `${translations.cancellation.cancellation} - ${translations.enums.cancellationMode[CancellationMode[CancellationMode.Positions]]}`,
            "modal-lg",
            true
        );
        this.isAdmin = isAdmin;
        this.doNotShowRefundToggles = doNotShowRefundToggles;

        this.onOrderCancellationSucceeded = onOrderCancellationSucceeded;
        this.onOrderCancellationFailed = onOrderCancellationFailed;

        this.onOrderCancellationSucceeded = onOrderCancellationSucceeded;
        this.onOrderCancellationFailed = onOrderCancellationFailed;

        this.$element.addClass("cancellation-positions");
        this.orderId = orderId;
        this.dao = Factory.getInstance<OrderDao>(OrderDao);

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);
        this.$form.append(`<div class="centered">${translations.cancellation.loading}</div>`);

        const initProm: any[] = [];
        initProm.push(
            this.dao.generateCancellationPositions(this.orderId).then((s) => {
                this.cancellationPositions = s;
            })
        );

        initProm.push(
            this.dao.getSingle(this.orderId, { showLocationOrderItemsIfLocationIsNotOwner: true }).then((s) => {
                this.order = s;
            })
        );

        Promise.all(initProm)
            .then(() => {
                this.$form.empty();
                this.setPositions();

                if (this.showRefundModeToggle()) {
                    this.$refundModeToggleContainer.show();
                } else {
                    this.$refundModeToggleContainer.hide();
                }

                if (this.showRefundToggle()) {
                    this.$refundToggleContainer.show();
                } else {
                    this.$refundToggleContainer.hide();
                }
                
            })
            .catch((ex) => {
                logError(ex);
                UiNotification.showException(translations.cancellation.failedLoading);
                this.close();
            });

        this.$refundContainer = $('<div class="refund-container"></div>').appendTo(this.$footer!);

        this.$refundModeToggleContainer = $('<div class="refund-toggle-container mr-auto" />')
            .appendTo(this.$refundContainer).hide();

        this.refundModeToggle = new Switch($('<input data-switch="true" type="checkbox" name="refundmode" />')
            .appendTo(this.$refundModeToggleContainer))
            .renderTo(this.$refundModeToggleContainer);
        this.$refundModeToggleContainer.append(`<label class="form-label">${translations.cancellation.offlineMode}</label>`);

        this.$refundToggleContainer = $('<div class="refund-toggle-container mr-auto" />')
            .appendTo(this.$refundContainer).hide();

        this.refundToggle = new Switch($('<input data-switch="true" type="checkbox" name="refund" />')
            .appendTo(this.$refundToggleContainer))
            .renderTo(this.$refundToggleContainer);

        this.$refundToggleContainer.append(`<label class="form-label">${translations.cancellation.refund}</label>`);

        $('<button type="button" class="btn btn-secondary">' + translations.cancellation.notNow + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $(
            '<button type="button" class="btn btn-danger">' + translations.cancellation.submitBtn + "</button>"
        )
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSubmit();
            return false;
        });
    }

    private setPositions() {
        this.$positions = $('<div class="positions scrollable" />').appendTo(this.$form);
        for (const cancellationOrderItem of this.cancellationPositions) {
            $(`<div>${translations.cancellation.positionSectionTitle.replace("{{itemName}}", this.getOrderItemName(cancellationOrderItem) ?? "")}</div>`)
                .appendTo(this.$positions);

            for (const item of cancellationOrderItem.positions) {
                const positions = this.renderCancellablePositionSection(item);
                if (positions === undefined) continue;

                positions.forEach((pos) => pos.appendTo(this.$positions!));
            }

            this.$positions.append($(`<div class="footer">${translations.general.of} ${cancellationOrderItem.maxCancellableQuantity}</div>`));
        }


        this.checkButtonState();
        this.onResize();
    }

    private getOrderItemName(position: ICancellationOrderItemDraft): string | undefined {
        return position.orderItemInternalName !== undefined
            ? position.orderItemInternalName
            : Format.htmlEscape(translate(position.orderItemName));
    }

    protected onShow() {
        $(window).bind("resize.cancellation", () => this.onResize());
        this.onResize();
    }

    protected onHide() {
        $(window).unbind("resize.cancellation");

        this.amountInput?.destroy();
        if (this.$positions !== undefined) {
            this.$positions.find("> .position").each((i, e) => {
                $(e).data("qtyctrl")?.destroy();
            });
        }

        this.$element.remove();
    }

    private onSubmit() {
        const data: IAddCancellationCmd = {
            mode: CancellationMode.Positions,
            refundMode: RefundMode.Online,
            isRefundApprovedByOperator: false,
        };

        if (this.showRefundToggle()) {
            data.isRefundApprovedByOperator = this.refundToggle.getValue()!;
        } else if (this.showRefundModeToggle()) {
            data.refundMode = this.refundModeToggle.getValue() ? RefundMode.Offline : RefundMode.Online;
        }
        
        //validate modes
        let hasError = false;
        this.$form.find("is-invalid").removeClass("is-invalid");

        data.positions = new Array<IAddCancellationPositionData>();

        this.$positions!.find("> .position").each((i, e) => {
            const qtyInput: NumberInput = $(e).data("qtyctrl");
            if (qtyInput != null) {
                const amt = qtyInput.getValue()!;
                if (amt > 0) {
                    data.positions!.push({
                        positionType: CancellationPositionType.OrderItem,
                        orderItemId: $(e).data("itemId"),
                        billId: $(e).data("billId"),
                        quantity: amt,
                        paymentId: $(e).data("paymentId"),
                    });
                }
            }
        });

        if (data.positions.length === 0) hasError = true;

        if (hasError) return;

        // start process of cancellation:
        // 1. select cancellation reason
        // 2. confirm if stock should be changed (if applicable)
        // 3. confirm if you are sure
        // 4. add cancellation
        showSelectCancellationReasonModal({
            locationId: this.order!.locationId,
            orderId: this.orderId,
            cancellableAmount: this.cancellableAmount!,
            data: data,
            onSaveStart: () => {
                this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true);
            },
            onSaveError: (errorCode: string, errorMessage: string) => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                UiNotification.showException(errorMessage, undefined, () => {
                    this.onOrderCancellationFailed(errorCode);
                    this.close();
                });
            },
            onSaveEnd: () => {
                this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
                this.onOrderCancellationSucceeded();
                this.close();
            },
        });
    }

    private renderCancellablePositionSection(item: ICancellationOrderItemPositionDraft): JQuery[] {
        const positions = new Array<JQuery>();
        positions.push(this.renderCancellablePositionsHeader());

        positions.push(this.renderCancellableItemPosition(
            item.orderItemId,
            item.cancellableQuantity,
            item.maxCancellableQuantity,
            item.billId,
            item.billName,
            item.payment));
        return positions;
    }

    private renderCancellablePositionsHeader(): JQuery {
        const header = $('<div class="position header" />');
        $('<div class="qty-input"></div>').appendTo(header);
        $('<div class="quantity" >' + translations.cancellation.quantity + "</div>").appendTo(header);
        $('<div class="bill" >' + translations.cancellation.bill + "</div>").appendTo(header);
        $('<div class="is-paid">' + translations.cancellation.status + "</div>").appendTo(header);
        $('<div class="created">' + translations.cancellation.payment + "</div>").appendTo(header);
        $('<div class="amt">' + translations.cancellation.totalIncTip + "</div>").appendTo(header);
        $('<div class="ccinfo">' + translations.cancellation.paymentMethod + "</div>").appendTo(header);
        $('<div class="created-by">' + translations.cancellation.user + "</div>").appendTo(header);

        return header;
    }

    private renderCancellableItemPosition(
        orderItemId: string,
        cancellableQty: number,
        maxCancellableQty: number,
        billId: string | undefined,
        billName: string | undefined,
        positionPayment?: ICancellationPositionPayment
    ): JQuery {
        const position = $('<div class="position item" />').data("itemId", orderItemId).data("billId", billId);
        if (positionPayment !== undefined) {
            position.data("paymentId", positionPayment.paymentId);
        }

        const qtyInputContainer = $('<div class="qty-input"></div>').appendTo(position);
        const qtyInputField = $('<input class="form-control" value="0" />').appendTo(qtyInputContainer);

        const qtyInput = new NumberInput(qtyInputField, {
            min: 0,
            max: maxCancellableQty < cancellableQty ? maxCancellableQty : cancellableQty,
            step: 1,
            decimals: 0,
        });

        qtyInput.onChange = () => this.checkButtonState();

        position.data("qtyctrl", qtyInput);
        $('<div class="quantity" >' + cancellableQty + "x</div>").appendTo(position);

        $(
            '<div class="bill">' +
                (billName == null ? " - " : billName) +
                "</div>"
        ).appendTo(position);

        $(
            '<div class="is-paid">' +
                (positionPayment == null ? translations.cancellation.notPaid : translations.cancellation.paid) +
                "</div>"
        ).appendTo(position);

        $(
            '<div class="created">' +
                (positionPayment == null
                    ? "-"
                    : DateTime.toPreciseDateTimeString(positionPayment.confirmedDateTime ?? positionPayment.created)) +
                "</div>"
        ).appendTo(position);

        const amt = positionPayment === undefined ? 0 : positionPayment.totalInclTip;
        $('<div class="amt">' + (amt === 0 ? "-" : "CHF " + amt.toFixed(2)) + "</div>").appendTo(position);

        $(
            positionPayment == null
                ? '<div class="ccinfo">-</div>'
                : PaymentMethodUtil.generatePaymentMethodDivIconFromCancellationPositionPayment(translations.general.unknown, positionPayment)
        ).appendTo(position);

        let createdBy = positionPayment?.createdBy;
        if (this.isAdmin) {
            if (createdBy === "Anonymer User") {
                createdBy = translations.general.anonymousUser;
            }
        }

        if (createdBy == null) createdBy = "-";

        $('<div class="created-by">' + createdBy + "</div>").appendTo(position);

        return position;
    }

    private checkButtonState() {
        this.cancellableAmount = 0;
        let isEnabled = false;

        this.$positions!.find("> .position").each((i, e) => {
            const qtyInput: NumberInput = $(e).data("qtyctrl");
            if (qtyInput != null) {
                const quantityToCancel = qtyInput.getValue()!;
                if (quantityToCancel > 0) {
                    if (!isEnabled) isEnabled = true;

                    if ($(e).hasClass("tip")) {
                        this.cancellableAmount! += this.order!.tip;
                    } else {
                        const orderItemId = $(e).data("itemId");
                        const orderItem = this.order!.items.find((c) => c.id === orderItemId);
                        if (orderItem) {
                            this.cancellableAmount! += orderItem.unitDiscountedPriceWithOptions! * quantityToCancel;
                        }
                    }
                }
            }
        });

        this.$submitBtn.html(`${translations.cancellation.submitBtn} - CHF ${this.cancellableAmount.toFixed(2)}`);

        if (isEnabled) this.$submitBtn.removeClass("disabled").removeAttr("disabled");
        else this.$submitBtn.addClass("disabled").attr("disabled", "disabled");
    }

    private onResize() {
        if (this.$positions === undefined) return;

        const windowHeight = $(window).height();
        this.$positions.removeAttr("style");
        const currentHeight = this.$positions.height();
        const maxHeight = windowHeight - 300;
        if (currentHeight > maxHeight) this.$positions.height(maxHeight);
    }

    private showRefundModeToggle(): boolean {
        return !this.doNotShowRefundToggles && this.isAdmin && this.order?.refundabilityType === RefundabilityType.Refundable;
    }

    private showRefundToggle(): boolean {
        return this.order?.refundabilityType === RefundabilityType.RefundOptionalForOperators;
    }
}
